import React, { useState } from "react";
import JSZip from "jszip";
import { UploadZone, ResetButton, humanBytes, loadImage } from "./_shared";
import { Slider } from "@/components/ui/slider";
import { Download, Loader2, X } from "lucide-react";
import { TOOL } from "@/constants/testIds";

export default function BatchCompressor() {
  const [files, setFiles] = useState([]);
  const [quality, setQuality] = useState(0.7);
  const [processed, setProcessed] = useState([]); // {name, blob, size, origSize}
  const [busy, setBusy] = useState(false);

  const compressOne = async (f, q) => {
    const { img } = await loadImage(f);
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0);
    const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", q));
    return { name: f.name.replace(/\.[^.]+$/, "") + ".jpg", blob, size: blob.size, origSize: f.size };
  };

  const process = async (list, q = quality) => {
    setBusy(true);
    const out = [];
    for (const f of list) {
      // eslint-disable-next-line no-await-in-loop
      out.push(await compressOne(f, q));
    }
    setProcessed(out);
    setBusy(false);
  };

  const handleFiles = async (fs) => {
    const next = [...files, ...fs];
    setFiles(next);
    await process(next, quality);
  };

  const removeAt = async (i) => {
    const next = files.filter((_, idx) => idx !== i);
    setFiles(next);
    if (next.length) await process(next); else setProcessed([]);
  };

  const downloadZip = async () => {
    setBusy(true);
    const zip = new JSZip();
    processed.forEach((p) => zip.file(p.name, p.blob));
    const blob = await zip.generateAsync({ type: "blob" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "imagekithub-batch.zip";
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    setBusy(false);
  };

  const reset = () => { setFiles([]); setProcessed([]); };

  const totalOrig = files.reduce((s, f) => s + f.size, 0);
  const totalNew = processed.reduce((s, p) => s + p.size, 0);
  const saved = totalOrig ? Math.round((1 - totalNew / totalOrig) * 100) : 0;

  if (files.length === 0) {
    return <UploadZone multiple onFiles={handleFiles} hint="Drop multiple images — we'll compress and package them as a zip" />;
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200 dark:border-white/10 p-6 bg-white dark:bg-[#121316]">
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Quality</label>
          <span className="text-sm text-slate-500 dark:text-slate-400">{Math.round(quality * 100)}%</span>
        </div>
        <Slider
          value={[quality * 100]}
          min={10} max={100} step={5}
          onValueChange={(v) => setQuality(v[0] / 100)}
          onValueCommit={() => process(files, quality)}
          data-testid="batch-quality-slider"
        />
      </div>

      <div className="rounded-2xl border border-slate-200 dark:border-white/10 overflow-hidden">
        <div className="grid grid-cols-4 gap-2 px-4 py-3 border-b border-slate-200 dark:border-white/10 text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400">
          <span className="col-span-2">File</span>
          <span>Original</span>
          <span>Compressed</span>
        </div>
        {files.map((f, i) => {
          const p = processed[i];
          return (
            <div key={i} className="grid grid-cols-4 gap-2 items-center px-4 py-3 border-b last:border-b-0 border-slate-100 dark:border-white/5 text-sm">
              <span className="col-span-2 truncate text-slate-900 dark:text-white">{f.name}</span>
              <span className="text-slate-500 dark:text-slate-400">{humanBytes(f.size)}</span>
              <span className="flex items-center justify-between gap-2">
                <span className="text-emerald-600 dark:text-emerald-400">
                  {p ? humanBytes(p.size) : busy ? "…" : "—"}
                </span>
                <button onClick={() => removeAt(i)} className="p-1 text-slate-400 hover:text-red-500" aria-label="Remove"><X size={14} /></button>
              </span>
            </div>
          );
        })}
      </div>

      <UploadZone multiple onFiles={handleFiles} hint="Add more images" />

      {processed.length > 0 && (
        <div className="rounded-2xl border border-slate-200 dark:border-white/10 p-6 bg-white dark:bg-[#121316] flex flex-wrap items-center justify-between gap-4">
          <div className="text-sm">
            <div className="font-medium text-slate-900 dark:text-white">{processed.length} files ready</div>
            <div className="text-slate-500 dark:text-slate-400 mt-0.5">
              {humanBytes(totalOrig)} → {humanBytes(totalNew)} <span className="text-emerald-600 dark:text-emerald-400 font-medium">(-{saved}%)</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={downloadZip}
              disabled={busy}
              data-testid={TOOL.downloadButton}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-medium transition-colors"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
              {busy ? "Building zip…" : "Download all (.zip)"}
            </button>
            <ResetButton onClick={reset} />
          </div>
        </div>
      )}
    </div>
  );
}
